// Blog Images API Client
import { request, uploadFile, getImageUrl as resolveImageUrl, type ApiResponse } from "./base";

// ============================================================================
// TYPES
// ============================================================================

export type BlogImageSourceType = "upload" | "pexels" | "project";

export type BlogImage = {
	id: number;
	blogId: string;
	sourceType: BlogImageSourceType;
	sourceId: string | null;
	storageKey: string; // R2 key or Pexels URL
	thumbnailKey: string | null;
	altText: string; // Required for SEO
	caption: string | null;
	width: number | null;
	height: number | null;
	fileSize: number | null;
	sortOrder: number;
	isUsedInContent: boolean;
	dateCreated: number;
	dateUpdated: number;
};

export type PexelsPhoto = {
	id: number;
	width: number;
	height: number;
	url: string;
	photographer: string;
	photographer_url: string;
	photographer_id: number;
	avg_color: string;
	src: {
		original: string;
		large2x: string;
		large: string;
		medium: string;
		small: string;
		portrait: string;
		landscape: string;
		tiny: string;
	};
	liked: boolean;
	alt: string;
};

export type PexelsSearchResult = {
	page: number;
	per_page: number;
	photos: PexelsPhoto[];
	total_results: number;
	next_page?: string;
	prev_page?: string;
};

export type UploadBlogImageInput = {
	file: File;
	altText: string;
	caption?: string;
};

export type AddPexelsImageInput = {
	pexelsId: string;
	url: string;
	width: number;
	height: number;
	altText: string;
	photographer?: string;
	photographerUrl?: string;
};

export type AddProjectImageInput = {
	projectPhotoId: number;
	altText: string;
	caption?: string;
};

export type UpdateBlogImageInput = {
	altText?: string;
	caption?: string;
};

// ============================================================================
// API CLIENT
// ============================================================================

export const blogImagesApi = {
	/**
	 * Get all images for a blog
	 * @param proId - Pro ID
	 * @param blogId - Blog ID
	 * @returns Array of blog images sorted by sortOrder
	 */
	async getBlogImages(
		proId: string,
		blogId: string,
	): Promise<ApiResponse<BlogImage[]>> {
		return request<BlogImage[]>(`/api/pro/${proId}/blogs/my-blogs/${blogId}/images`);
	},

	/**
	 * Upload a new image to R2
	 * @param proId - Pro ID
	 * @param blogId - Blog ID
	 * @param input - Upload input (file, altText, optional caption)
	 * @returns Uploaded blog image record
	 */
	async uploadBlogImage(
		proId: string,
		blogId: string,
		input: UploadBlogImageInput,
	): Promise<ApiResponse<BlogImage>> {
		return uploadFile<BlogImage>(
			`/api/pro/${proId}/blogs/my-blogs/${blogId}/images/upload`,
			input.file,
			{
				altText: input.altText,
				...(input.caption && { caption: input.caption }),
			},
		);
	},

	/**
	 * Add a Pexels stock photo to the gallery
	 * @param proId - Pro ID
	 * @param blogId - Blog ID
	 * @param input - Pexels image data
	 * @returns Blog image record
	 */
	async addPexelsImage(
		proId: string,
		blogId: string,
		input: AddPexelsImageInput,
	): Promise<ApiResponse<BlogImage>> {
		return request<BlogImage>(
			`/api/pro/${proId}/blogs/my-blogs/${blogId}/images/pexels`,
			{
				method: "POST",
				body: {
					pexelsId: input.pexelsId,
					url: input.url,
					width: input.width,
					height: input.height,
					altText: input.altText,
					photographer: input.photographer,
					photographerUrl: input.photographerUrl,
				},
			},
		);
	},

	/**
	 * Copy a project photo to the blog gallery
	 * @param proId - Pro ID
	 * @param blogId - Blog ID
	 * @param input - Project photo data
	 * @returns Blog image record
	 */
	async addProjectImage(
		proId: string,
		blogId: string,
		input: AddProjectImageInput,
	): Promise<ApiResponse<BlogImage>> {
		return request<BlogImage>(
			`/api/pro/${proId}/blogs/my-blogs/${blogId}/images/project`,
			{
				method: "POST",
				body: {
					projectPhotoId: input.projectPhotoId,
					altText: input.altText,
					caption: input.caption,
				},
			},
		);
	},

	/**
	 * Search Pexels for stock photos
	 * @param proId - Pro ID (for auth)
	 * @param query - Search query
	 * @param page - Page number (default: 1)
	 * @param perPage - Results per page (default: 20, max: 80)
	 * @returns Pexels search results
	 */
	async searchPexels(
		proId: string,
		query: string,
		page = 1,
		perPage = 20,
	): Promise<ApiResponse<PexelsSearchResult>> {
		const params = new URLSearchParams({
			q: query,
			page: page.toString(),
			per_page: perPage.toString(),
		});

		return request<PexelsSearchResult>(
			`/api/pro/${proId}/blogs/images/search/pexels?${params.toString()}`,
		);
	},

	/**
	 * Delete an image from the gallery
	 * @param proId - Pro ID
	 * @param blogId - Blog ID
	 * @param imageId - Image ID to delete
	 * @returns Success response
	 */
	async deleteImage(
		proId: string,
		blogId: string,
		imageId: number,
	): Promise<ApiResponse<{ message: string }>> {
		return request<{ message: string }>(
			`/api/pro/${proId}/blogs/my-blogs/${blogId}/images/${imageId}`,
			{
				method: "DELETE",
			},
		);
	},

	/**
	 * Update image metadata (alt text, caption)
	 * @param proId - Pro ID
	 * @param blogId - Blog ID
	 * @param imageId - Image ID to update
	 * @param input - Updated fields
	 * @returns Updated blog image record
	 */
	async updateImage(
		proId: string,
		blogId: string,
		imageId: number,
		input: UpdateBlogImageInput,
	): Promise<ApiResponse<BlogImage>> {
		return request<BlogImage>(
			`/api/pro/${proId}/blogs/my-blogs/${blogId}/images/${imageId}`,
			{
				method: "PUT",
				body: input,
			},
		);
	},

	/**
	 * Helper: Get image URL for display
	 * For upload/project sources, constructs /api/images/{storageKey}
	 * For pexels sources, returns the direct URL
	 * @param image - Blog image record
	 * @returns Full image URL
	 */
	getImageUrl(image: BlogImage): string {
		if (image.sourceType === "pexels") {
			return image.storageKey; // Direct Pexels URL
		}
		// Use base helper to prepend API base URL for relative paths
		return resolveImageUrl(image.storageKey);
	},

	/**
	 * Helper: Validate alt text
	 * @param altText - Alt text to validate
	 * @returns Validation result
	 */
	validateAltText(altText: string): {
		valid: boolean;
		error?: string;
	} {
		// Empty alt text is allowed — users can add it later
		if (!altText || altText.trim().length === 0) {
			return { valid: true };
		}

		if (altText.length < 10) {
			return {
				valid: false,
				error: "Alt text must be at least 10 characters for SEO",
			};
		}

		if (altText.length > 125) {
			return {
				valid: false,
				error: "Alt text must be less than 125 characters",
			};
		}

		return { valid: true };
	},
};
